"use client";

import { c as _c } from "react/compiler-runtime";
import { TableCell, TableRow } from "../index";
import { AnimatePresence, motion } from "motion/react";
import React from "react";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import Button from "@/components/ui/button";
import { cx } from "@/lib/tailwind-utils";
import { useAnalyticsGroupedTable } from "./context";
import type { AnalyticsGroup, AnalyticsRow, AnalyticsSummary } from "./types";

// ============================================================================
// AnalyticsDataRow
// ============================================================================

interface AnalyticsDataRowProps<TRow extends AnalyticsRow> {
  row: TRow;
  groupKey?: string;
  index?: number;
}
export function AnalyticsDataRow(t0) {
  const $ = _c(26);
  const {
    row,
    groupKey,
    index
  } = t0;
  const {
    visibleColumns,
    isGrouped,
    onRowClick,
    getRowKey
  } = useAnalyticsGroupedTable();
  let t1;
  if ($[0] !== getRowKey || $[1] !== groupKey || $[2] !== index || $[3] !== row) {
    t1 = getRowKey ? getRowKey(row) : `row-${groupKey}-${index}`;
    $[0] = getRowKey;
    $[1] = groupKey;
    $[2] = index;
    $[3] = row;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const key = t1;
  const t2 = onRowClick && "cursor-pointer hover:bg-row-hover";
  let t3;
  if ($[5] !== t2) {
    t3 = cx("transition-colors duration-200", t2);
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== groupKey || $[8] !== onRowClick || $[9] !== row) {
    t4 = () => onRowClick?.(row, groupKey);
    $[7] = groupKey;
    $[8] = onRowClick;
    $[9] = row;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== isGrouped) {
    t5 = isGrouped && <TableCell />;
    $[11] = isGrouped;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== groupKey || $[14] !== row || $[15] !== visibleColumns) {
    let t7;
    if ($[17] !== groupKey || $[18] !== row) {
      t7 = col => <TableCell key={col.key} className={col.cellClassName}>{col.renderCell(row, groupKey)}</TableCell>;
      $[17] = groupKey;
      $[18] = row;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    t6 = visibleColumns.map(t7);
    $[13] = groupKey;
    $[14] = row;
    $[15] = visibleColumns;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[20] !== key || $[21] !== t3 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t7 = <TableRow key={key} className={t3} onClick={t4}>{t5}{t6}</TableRow>;
    $[20] = key;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
}

// ============================================================================
// AnalyticsSummaryRow
// ============================================================================

interface AnalyticsSummaryRowProps<TSummary extends AnalyticsSummary> {
  summary: TSummary;
  label: string;
  isGrandTotal?: boolean;
}
export function AnalyticsSummaryRow(t0) {
  const $ = _c(19);
  const {
    summary,
    label,
    isGrandTotal: t1
  } = t0;
  const isGrandTotal = t1 === undefined ? false : t1;
  const {
    visibleColumns,
    isGrouped,
    renderSummaryLabel
  } = useAnalyticsGroupedTable();
  const t2 = isGrandTotal ? "border-t-2 bg-muted" : "border-t bg-background-secondary";
  let t3;
  if ($[0] !== t2) {
    t3 = cx("font-semibold", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== isGrouped) {
    t4 = isGrouped && <TableCell />;
    $[2] = isGrouped;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== isGrandTotal || $[5] !== label || $[6] !== renderSummaryLabel || $[7] !== summary || $[8] !== visibleColumns) {
    let t6;
    if ($[10] !== isGrandTotal || $[11] !== label || $[12] !== renderSummaryLabel || $[13] !== summary) {
      t6 = (col, index) => <TableCell key={col.key} className={cx(col.cellClassName, isGrandTotal ? "font-bold" : "font-medium")}>{index === 0 ? renderSummaryLabel ? renderSummaryLabel(label, isGrandTotal) : <span className="text-body">{label}</span> : col.renderSummary(summary, isGrandTotal)}</TableCell>;
      $[10] = isGrandTotal;
      $[11] = label;
      $[12] = renderSummaryLabel;
      $[13] = summary;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t5 = visibleColumns.map(t6);
    $[4] = isGrandTotal;
    $[5] = label;
    $[6] = renderSummaryLabel;
    $[7] = summary;
    $[8] = visibleColumns;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
    t6 = <TableRow className={t3}>{t4}{t5}</TableRow>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}

// ============================================================================
// AnalyticsGroupRow
// ============================================================================

interface AnalyticsGroupRowProps<TRow extends AnalyticsRow, TSummary extends AnalyticsSummary> {
  group: AnalyticsGroup<TRow, TSummary>;
}
export function AnalyticsGroupRow(t0) {
  const $ = _c(66);
  const {
    group
  } = t0;
  const {
    visibleColumns,
    expandedGroups,
    toggleGroup,
    itemLabel,
    onRowClick,
    getRowKey,
    renderGroupHeader,
    pagination
  } = useAnalyticsGroupedTable();
  let T0;
  let T1;
  let hasPagination;
  let isExpanded;
  let isGroupLoading;
  let t1;
  let t2;
  let t3;
  if ($[0] !== expandedGroups || $[1] !== getRowKey || $[2] !== group || $[3] !== pagination || $[4] !== renderGroupHeader || $[5] !== toggleGroup) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      isExpanded = expandedGroups.has(group.key);
      hasPagination = pagination && group.totalPages && group.totalPages > 1;
      let t4;
      if ($[14] !== group.key || $[15] !== pagination) {
        t4 = pagination?.isGroupLoading(group.key) ?? false;
        $[14] = group.key;
        $[15] = pagination;
        $[16] = t4;
      } else {
        t4 = $[16];
      }
      isGroupLoading = t4;
      if (renderGroupHeader) {
        let t5;
        if ($[17] !== group.key || $[18] !== toggleGroup) {
          t5 = () => toggleGroup(group.key);
          $[17] = group.key;
          $[18] = toggleGroup;
          $[19] = t5;
        } else {
          t5 = $[19];
        }
        t3 = <React.Fragment key={group.key}>{renderGroupHeader(group, isExpanded, t5)}<AnimatePresence initial={false}>{isExpanded && <>{group.rows.map((row, idx) => <AnalyticsDataRow key={getRowKey ? getRowKey(row) : `row-${group.key}-${idx}`} row={row} groupKey={group.key} index={idx} />)}</>}</AnimatePresence></React.Fragment>;
        break bb0;
      }
      T1 = React.Fragment;
      t2 = group.key;
      T0 = TableRow;
      t1 = cx("cursor-pointer bg-background-secondary transition-colors hover:bg-hover", isExpanded && "border-b-0");
    }
    $[0] = expandedGroups;
    $[1] = getRowKey;
    $[2] = group;
    $[3] = pagination;
    $[4] = renderGroupHeader;
    $[5] = toggleGroup;
    $[6] = T0;
    $[7] = T1;
    $[8] = hasPagination;
    $[9] = isExpanded;
    $[10] = isGroupLoading;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    T0 = $[6];
    T1 = $[7];
    hasPagination = $[8];
    isExpanded = $[9];
    isGroupLoading = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[20] !== group.key || $[21] !== toggleGroup) {
    t4 = () => toggleGroup(group.key);
    $[20] = group.key;
    $[21] = toggleGroup;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  const t5 = isExpanded ? "Collapse group" : "Expand group";
  let t6;
  if ($[23] !== isExpanded) {
    t6 = isExpanded ? <FaChevronDown className="h-3 w-3" /> : <FaChevronRight className="h-3 w-3" />;
    $[23] = isExpanded;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== isExpanded || $[26] !== t5 || $[27] !== t6) {
    t7 = <TableCell className="w-10"><button type="button" className="flex h-6 w-6 items-center justify-center rounded text-foreground-secondary hover:text-foreground" aria-expanded={isExpanded} aria-label={t5}>{t6}</button></TableCell>;
    $[25] = isExpanded;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== group.count || $[30] !== group.label || $[31] !== group.summary || $[32] !== itemLabel || $[33] !== visibleColumns) {
    let t9;
    if ($[35] !== group.count || $[36] !== group.label || $[37] !== group.summary || $[38] !== itemLabel) {
      t9 = (col, index) => <TableCell key={col.key} className={cx(index === 0 ? "font-medium" : cx(col.cellClassName, "font-medium"))}>{index === 0 ? <div className="flex items-center gap-2"><span className="whitespace-nowrap font-semibold">{group.label}</span>{itemLabel && <span className="whitespace-nowrap text-caption text-foreground-muted">({group.count} {itemLabel})</span>}</div> : col.renderSummary(group.summary, false)}</TableCell>;
      $[35] = group.count;
      $[36] = group.label;
      $[37] = group.summary;
      $[38] = itemLabel;
      $[39] = t9;
    } else {
      t9 = $[39];
    }
    t8 = visibleColumns.map(t9);
    $[29] = group.count;
    $[30] = group.label;
    $[31] = group.summary;
    $[32] = itemLabel;
    $[33] = visibleColumns;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[40] !== T0 || $[41] !== t1 || $[42] !== t4 || $[43] !== t7 || $[44] !== t8) {
    t9 = <T0 className={t1} onClick={t4}>{t7}{t8}</T0>;
    $[40] = T0;
    $[41] = t1;
    $[42] = t4;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  let t10;
  if ($[46] !== getRowKey || $[47] !== group.count || $[48] !== group.key || $[49] !== group.rows || $[50] !== group.totalPages || $[51] !== hasPagination || $[52] !== isExpanded || $[53] !== isGroupLoading || $[54] !== itemLabel || $[55] !== onRowClick || $[56] !== pagination || $[57] !== visibleColumns) {
    t10 = isExpanded && <>{isGroupLoading ? Array.from({
        length: pagination?.pageSize ?? 10
      }).map((_, index_0) => <motion.tr key={`skeleton-${group.key}-${index_0}`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="animate-pulse"><td className="w-10" />{visibleColumns.map(_temp)}</motion.tr>) : group.rows.map((row_0, idx_0) => <motion.tr key={getRowKey ? getRowKey(row_0) : `row-${group.key}-${idx_0}`} initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: "auto"
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.15
      }} className={cx("bg-background transition-colors duration-200", onRowClick && "cursor-pointer hover:bg-row-hover")} onClick={() => onRowClick?.(row_0, group.key)}><td className="w-10" />{visibleColumns.map(col_1 => <td key={col_1.key} className={cx("truncate py-2 text-body text-foreground-secondary", col_1.cellClassName)}>{col_1.renderCell(row_0, group.key)}</td>)}</motion.tr>)}{hasPagination && (() => {
        const currentPage = pagination.getGroupPage(group.key);
        const totalPages = group.totalPages ?? 1;
        const start = (currentPage - 1) * pagination.pageSize + 1;
        const end = Math.min(currentPage * pagination.pageSize, group.count);
        return <tr><td colSpan={visibleColumns.length + 1} className="p-0"><div className="flex items-center justify-between border-t border-border px-4 py-1 text-body text-foreground-secondary"><span className="font-semibold">{start.toLocaleString()}-{end.toLocaleString()} of{" "}{group.count.toLocaleString()} {itemLabel}</span><div className="flex items-center gap-1"><Button variant="ghost" icon={HiChevronLeft} onClick={() => pagination.setGroupPage(group.key, currentPage - 1)} disabled={currentPage === 1 || isGroupLoading} aria-label="Previous page" /><span className="px-1 font-semibold tabular-nums">Page {currentPage} of {totalPages}</span><Button variant="ghost" icon={HiChevronRight} onClick={() => pagination.setGroupPage(group.key, currentPage + 1)} disabled={currentPage === totalPages || isGroupLoading} aria-label="Next page" /></div></div></td></tr>;
      })()}</>;
    $[46] = getRowKey;
    $[47] = group.count;
    $[48] = group.key;
    $[49] = group.rows;
    $[50] = group.totalPages;
    $[51] = hasPagination;
    $[52] = isExpanded;
    $[53] = isGroupLoading;
    $[54] = itemLabel;
    $[55] = onRowClick;
    $[56] = pagination;
    $[57] = visibleColumns;
    $[58] = t10;
  } else {
    t10 = $[58];
  }
  let t11;
  if ($[59] !== t10) {
    t11 = <AnimatePresence initial={false}>{t10}</AnimatePresence>;
    $[59] = t10;
    $[60] = t11;
  } else {
    t11 = $[60];
  }
  let t12;
  if ($[61] !== T1 || $[62] !== t11 || $[63] !== t2 || $[64] !== t9) {
    t12 = <T1 key={t2}>{t9}{t11}</T1>;
    $[61] = T1;
    $[62] = t11;
    $[63] = t2;
    $[64] = t9;
    $[65] = t12;
  } else {
    t12 = $[65];
  }
  return t12;
}
function _temp(col_0) {
  return <td key={col_0.key} className={cx("truncate py-2 text-body", col_0.cellClassName)}><div className="h-4 rounded bg-muted" /></td>;
}